import { IonIcon } from "@ionic/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/button";
import { qrCodeIcon } from "@/lib/icons/qr-code.icon";
import { cx } from "@/lib/style/cva.config";

interface QRClockButtonsProps {
  shiftStartAt: string;
  shiftEndAt: string;
  isClockedIn: boolean;
  isClockedOut: boolean;
  onClockIn: () => void;
  onClockOut: () => void;
  isLoading?: boolean;
}

export function QRClockButtons({
  shiftStartAt,
  shiftEndAt,
  isClockedIn,
  isClockedOut,
  onClockIn,
  onClockOut,
  isLoading = false,
}: QRClockButtonsProps) {
  const { t } = useTranslation();

  const clockInButtonState = useMemo(() => {
    const reasons: string[] = [];
    let disabled = false;

    // Validate shift data
    if (!shiftStartAt || !shiftEndAt) {
      reasons.push("Missing shift data");
      disabled = true;
      return { disabled: true, canClockIn: false, reasons, debugInfo: {} };
    }

    const now = new Date();
    const shiftStart = new Date(shiftStartAt);
    const diffMs = shiftStart.getTime() - now.getTime();
    const hoursBeforeShift = diffMs / (1000 * 60 * 60);

    // Check conditions
    if (isClockedIn) {
      reasons.push("Already clocked in");
      disabled = true;
    }

    if (isClockedOut) {
      reasons.push("Shift already completed");
      disabled = true;
    }

    if (hoursBeforeShift > 24) {
      reasons.push(`Too early (${Math.round(hoursBeforeShift)} hours before shift)`);
      disabled = true;
    }

    if (isLoading) {
      reasons.push("Processing...");
      disabled = true;
    }

    // Can clock in if no disable reasons
    const canClockIn = reasons.length === 0;

    return {
      disabled: disabled || !canClockIn,
      canClockIn,
      reasons,
    };
  }, [shiftStartAt, shiftEndAt, isClockedIn, isClockedOut, isLoading]);

  const clockOutButtonState = useMemo(() => {
    const reasons: string[] = [];
    let disabled = false;

    if (!isClockedIn) {
      reasons.push("Not clocked in yet");
      disabled = true;
    }

    if (isClockedOut) {
      reasons.push("Already clocked out");
      disabled = true;
    }

    if (isLoading) {
      reasons.push("Processing...");
      disabled = true;
    }

    // Can clock out if no disable reasons
    const canClockOut = reasons.length === 0;

    return {
      disabled: disabled || !canClockOut,
      canClockOut,
      reasons,
    };
  }, [isClockedIn, isClockedOut, isLoading]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-3">
        <Button
          expand="block"
          disabled={clockInButtonState.disabled}
          onClick={onClockIn}
          className={cx("flex-1", clockInButtonState.disabled && "opacity-50")}
          title={
            clockInButtonState.disabled
              ? clockInButtonState.reasons.join(", ")
              : t("common:shift.clock-in.qr")
          }
        >
          <div className="flex items-center gap-2">
            <IonIcon icon={qrCodeIcon} className="size-5 text-white" />
            {t("common:shift.clock-in.qr")}
          </div>
        </Button>
        <Button
          expand="block"
          disabled={clockOutButtonState.disabled}
          onClick={onClockOut}
          className={cx("flex-1", clockOutButtonState.disabled && "opacity-50")}
          title={
            clockOutButtonState.disabled
              ? clockOutButtonState.reasons.join(", ")
              : t("common:shift.clock-out.qr")
          }
        >
          <div className="flex items-center gap-2">
            <IonIcon icon={qrCodeIcon} className="size-5" />
            {t("common:shift.clock-out.qr")}
          </div>
        </Button>
      </div>
    </div>
  );
}
